import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { StructuredData } from "@/lib/seo";

interface SeoProps {
  title: string;
  description: string;
  structuredData?: StructuredData[];
}

export default function Seo({ title, description, structuredData = [] }: SeoProps) {
  const { pathname } = useLocation();
  const structuredKey = JSON.stringify(structuredData);

  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}${pathname}`;

    document.title = title;

    const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
      let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);

    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    const existing = document.getElementById("page-structured-data");
    if (existing) existing.remove();

    const data: StructuredData[] = structuredKey ? JSON.parse(structuredKey) : [];
    if (data.length > 0) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-structured-data";
      script.textContent = JSON.stringify(data.length === 1 ? data[0] : data);
      document.head.appendChild(script);
    }
  }, [title, description, pathname, structuredKey]);

  return null;
}